import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { checkTypeStringRegexDatagrid, displayErrorMessage } from 'utils/tools_functions';
import Button from '@mui/material/Button';
import { inputRequired } from 'components/generics/Validators/validator';
import { validateFormData } from 'components/generics/Validators/form.validate';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  FormControl,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { StyledTooltipErrorInputCostModal } from 'utils/Datagrid/CustomCultureDataGridUtils';
import { customDialogAddEditCommonTheme } from 'assets/styles/themes/Dialog/generic_dialog_add_edit_parcel';
import { IMPORT_TYPE } from 'utils/GlobalVariables';
import { addCultureMappingData } from 'services/API/Cooperative';

const AddCultureMappingDataModal = ({
  isModalCreateOpen,
  setModalCreateOpen,
  cultureList,
  cooperativeId,
  mappingTypes,
  selectedMappingType,
  actions,
  refreshDataAfterNewFileImport,
  cultureMappingData,
}) => {
  const { t } = useTranslation();
  const DEFAULT_FORM = {
    externalName: '',
    culture: '',
    irrigatedCulture: false,
    excludedCulture: true,
    mappingType: selectedMappingType ?? mappingTypes?.[0] ?? IMPORT_TYPE.telepac,
  };
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errorsFormDefault, setErrorsFormDefault] = useState({
    formError: false,
    externalName: {
      message: '',
      validator: [inputRequired],
    },
  });
  const isOpen = useMemo(() => errorsFormDefault.formError, [errorsFormDefault]);

  const updateForm = (key, value) => {
    if (key === 'culture') {
      // when user set culture to null, we set the excludedCulture to true
      if (value === '') {
        setFormData({ ...formData, excludedCulture: true, [key]: value });
        // when user set culture as a valid culture and the previous selected culture was null, we set the excludedCulture to false
      } else if (value !== '' && formData.culture === '') {
        setFormData({ ...formData, excludedCulture: false, [key]: value });
        // in other cases we dont change the excludedCulture
      } else {
        setFormData({ ...formData, [key]: value });
      }
    } else {
      setFormData({ ...formData, [key]: value });
    }
  };

  const onSubmit = async (e) => {
    errorsFormDefault.formError = false;
    setErrorsFormDefault({ ...validateFormData(formData, errorsFormDefault) });
    if (!errorsFormDefault.formError) {
      try {
        setModalCreateOpen(false);
        const payload = {
          ...formData,
          culture: formData.culture === '' ? null : formData.culture,
        };
        const response = await addCultureMappingData(formData.mappingType, cooperativeId, payload);
        const newMappingData = [
          ...cultureMappingData?.[formData.mappingType],
          response?.data?.data,
        ];
        refreshDataAfterNewFileImport(formData.mappingType, newMappingData);
        toast.success(t('success.mappingCultureCreated', { name: formData.externalName }));
        setFormData(DEFAULT_FORM);
      } catch (error) {
        toast.error(error?.response?.data?.message ?? displayErrorMessage('ERR_UNEXPECTED'));
      }
    }
  };

  return (
    <ThemeProvider theme={customDialogAddEditCommonTheme}>
      <Dialog
        id="custom-addParcel-dialog"
        maxWidth="md"
        fullWidth
        open={isModalCreateOpen}
        onClose={() => {
          setModal(false);
        }}
      >
        <Box className="modal_header">
          <DialogTitle>{t('title.addMappingCulture')}</DialogTitle>
          <ClearIcon className="close-icon" onClick={() => setModalCreateOpen(false)} />
        </Box>
        <DialogContent>
          <Box>
            <InputLabel id="nameLabel" htmlFor="externalName">
              {t('label.externalName')}
            </InputLabel>
            {errorsFormDefault?.externalName.message && (
              <div>
                <StyledTooltipErrorInputCostModal
                  open={isOpen}
                  title={errorsFormDefault?.externalName.message}
                  placement="top"
                >
                  <span></span>
                </StyledTooltipErrorInputCostModal>
              </div>
            )}
            <TextField
              id="externalName"
              value={formData.externalName}
              onChange={(e) => {
                updateForm('externalName', checkTypeStringRegexDatagrid(e.target.value));
              }}
              placeholder={t('placeholder.enterName')}
              className={errorsFormDefault?.externalName.message ? 'MuiErrorForm' : ''}
            />
          </Box>

          <Box>
            <InputLabel id="cultureAssoliaLabel" htmlFor="culture">
              {t('label.assoliaCulture')}
            </InputLabel>
            <FormControl>
              <Select
                displayEmpty
                id="culture"
                onChange={(e) => updateForm('culture', e.target.value)}
                value={formData.culture}
                renderValue={(value) => {
                  if (value === '') {
                    return t('message.noCultureAssociated');
                  }
                  const selectedCulture = cultureList?.find((culture) => culture.value === value);
                  return selectedCulture ? selectedCulture.label : value;
                }}
              >
                {cultureList?.map((culture) => (
                  <MenuItem key={culture?.value} value={culture?.value}>
                    <ListItemText
                      primary={
                        culture?.label === '' ? t('message.noCultureSelected') : culture?.label
                      }
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <InputLabel name="irrigatedCulture">{t('label.irrigated')}</InputLabel>
            <Checkbox
              className="checkbox-irrigable dialog-checkbox"
              id="irrigatedCulture"
              label="irrigatedCulture"
              checked={formData.irrigatedCulture}
              onChange={(e) => {
                updateForm('irrigatedCulture', e.target.checked);
              }}
            />
          </Box>
          <Box>
            <InputLabel name="excludedCulture">{t('label.excluded')}</InputLabel>
            <Checkbox
              className="checkbox-irrigable dialog-checkbox"
              id="excludedCulture"
              label="excludedCulture"
              checked={formData.excludedCulture}
              onChange={(e) => {
                updateForm('excludedCulture', e.target.checked);
              }}
              disabled={formData.culture === ''}
            />
          </Box>
          <Box>
            <InputLabel id="mappingTypeLabel" htmlFor="mappingType">
              {t('label.mappingType')}
            </InputLabel>
            <FormControl>
              <Select
                id="mappingType"
                onChange={(e) => updateForm('mappingType', e.target.value)}
                value={formData.mappingType}
              >
                {actions?.map((action) => (
                  <MenuItem key={action?.name} value={action?.name}>
                    <ListItemText primary={action?.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <ThemeProvider theme={customButtonTheme}>
            <Button id="addCultureMappingButton" onClick={onSubmit}>
              {t('common.add')}
            </Button>
          </ThemeProvider>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AddCultureMappingDataModal;
